import { createContext, useContext, useState, useCallback, useRef } from 'react'
import React from 'react'

interface ReserveHeaderContextType {
  title: string
  progressPercentage: number
  setTitle: (title: string) => void
  setProgressPercentage: (progressPercentage: number) => void
  setHasBackButton: (hasBackButton: boolean) => void
  setOnBack: (cb: (() => void) | undefined) => void
  onBackRef: React.MutableRefObject<(() => void) | undefined>
  hasBackButton?: boolean
}

const ReserveHeaderContext = createContext<ReserveHeaderContextType>({
  title: 'خانه',
  progressPercentage: 0,
  setTitle: () => {},
  setProgressPercentage: () => {},
  setHasBackButton: () => {},
  setOnBack: () => {},
  onBackRef: { current: undefined },
})

const ReserveHeaderProvider = ({ children }: { children: React.ReactNode }) => {
  const [_title, _setTitle] = useState<string>('خانه')
  const [_progressPercentage, _setProgressPercentage] = useState<number>(0)
  const [_hasBackButton, _setHasBackButton] = useState<boolean | undefined>(
    false
  )
  const onBackRef = useRef<(() => void) | undefined>(undefined)

  const setTitle = useCallback((title: string) => {
    _setTitle(title)
  }, [])

  const setProgressPercentage = useCallback((progressPercentage: number) => {
    _setProgressPercentage(progressPercentage)
  }, [])

  const setHasBackButton = useCallback((hasBackButton: boolean) => {
    _setHasBackButton(hasBackButton)
  }, [])

  const setOnBack = useCallback((cb: (() => void) | undefined) => {
    onBackRef.current = cb
  }, [])

  const contextData: ReserveHeaderContextType = {
    title: _title,
    progressPercentage: _progressPercentage,
    hasBackButton: _hasBackButton,
    setTitle,
    setProgressPercentage,
    setHasBackButton,
    setOnBack,
    onBackRef,
  }

  return (
    <ReserveHeaderContext.Provider value={contextData}>
      {children}
    </ReserveHeaderContext.Provider>
  )
}

export const useReserveHeader = (): ReserveHeaderContextType => {
  const context = useContext(ReserveHeaderContext)

  if (!context) {
    throw new Error(
      'useReserveHeader must be used within a ReserveHeaderContextProvider'
    )
  }

  return context
}

export default ReserveHeaderProvider
